import React, { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { BookOpen, Search } from 'lucide-react';
import api from '@/utils/api';

/**
 * JournalAutocomplete - Autocomplete for journal names with ISSN auto-fill
 * 
 * Props:
 * - value: current journal name value
 * - onChange: callback when journal name changes
 * - onJournalSelect: callback when a journal is selected (receives {name, issn, qualis})
 * - placeholder: input placeholder text
 * - className: additional CSS classes
 */
const JournalAutocomplete = ({ 
  value, 
  onChange, 
  onJournalSelect,
  placeholder = "Digite o nome da revista...",
  className = ""
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!value || value.length < 2) {
        setSuggestions([]);
        return;
      }

      setLoading(true);
      try {
        const response = await api.get(`/api/journals/search?q=${encodeURIComponent(value)}`);
        setSuggestions(response.data.journals || []);
      } catch (error) {
        console.error('Error fetching journal suggestions:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounce);
  }, [value]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);
    setShowSuggestions(true);
    setHighlightedIndex(-1);
  };

  const handleSelectJournal = (journal) => {
    onChange(journal.name);
    if (onJournalSelect) {
      onJournalSelect(journal);
    }
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSelectJournal(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={() => value && value.length >= 2 && setShowSuggestions(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="pl-10"
          data-testid="journal-autocomplete-input"
        />
        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
          </div>
        )}
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div 
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
          data-testid="journal-suggestions"
        >
          <div className="p-2 text-xs text-gray-500 border-b bg-gray-50">
            Revistas já cadastradas ({suggestions.length})
          </div>
          {suggestions.map((journal, index) => (
            <div
              key={journal.name}
              onClick={() => handleSelectJournal(journal)}
              className={`px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors ${
                index === highlightedIndex 
                  ? 'bg-blue-50' 
                  : 'hover:bg-gray-50'
              }`}
              data-testid={`journal-suggestion-${index}`}
            >
              <div className="flex items-start gap-3">
                <BookOpen className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 truncate">
                    {journal.name}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                    {journal.issn && (
                      <span>ISSN: {journal.issn}</span>
                    )}
                    {journal.qualis && (
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        journal.qualis.startsWith('A') 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {journal.qualis}
                      </span>
                    )}
                    <span className="text-gray-400">
                      {journal.count} chamada{journal.count !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No suggestions message */}
      {showSuggestions && value && value.length >= 2 && suggestions.length === 0 && !loading && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500 text-sm">
          <BookOpen className="h-8 w-8 mx-auto mb-2 text-gray-300" />
          Nenhuma revista encontrada. Você pode cadastrar uma nova.
        </div>
      )}
    </div>
  );
};

export default JournalAutocomplete;
